/**
 * Per-installation values this SPA needs and must not bake into the build (task brief: "auth_url
 * and any virtual_id are per-installation configuration ... keep them configurable, hardcode
 * nothing"). Unlike Consent.Web's same-origin CloudFront proxy — which lets one build be promoted
 * dev -> stg -> prd unchanged (patient-consent.md §8) — this service's public API sits on a
 * different origin from the SPA by design (architecture/services/integration-faceheart.md §8, D-3:
 * CORS is restricted to a single configured webapp origin, which only makes sense for a
 * cross-origin caller). A Vite build-time env var would bake one environment's origin into the
 * bundle, so these are read at *runtime* from a small JSON file the deploy pipeline overwrites
 * per environment before `s3 sync` — the same "build once, template after" shape, moved from HTML
 * substitution to a fetched document because there is more than one string to place.
 */
export interface RuntimeConfig {
  readonly scanApiBaseUrl: string
  readonly authApiBaseUrl: string
  readonly faceHeartAuthUrl: string
  readonly faceHeartVirtualId: string | null
}

function isRuntimeConfig(value: unknown): value is RuntimeConfig {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.scanApiBaseUrl === 'string' &&
    typeof candidate.authApiBaseUrl === 'string' &&
    typeof candidate.faceHeartAuthUrl === 'string' &&
    (candidate.faceHeartVirtualId === null || typeof candidate.faceHeartVirtualId === 'string')
  )
}

export class RuntimeConfigError extends Error {}

export async function loadRuntimeConfig(
  fetchImpl: typeof fetch = fetch,
): Promise<RuntimeConfig> {
  const response = await fetchImpl('/config.json', { cache: 'no-store' })
  if (!response.ok) {
    throw new RuntimeConfigError(`/config.json answered ${response.status}`)
  }
  const body: unknown = await response.json()
  if (!isRuntimeConfig(body)) {
    throw new RuntimeConfigError('/config.json is not shaped like a RuntimeConfig')
  }
  return body
}
